import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '@kss/api';
import { Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';

const inputCls =
  'h-11 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-3 text-sm text-[var(--color-text-primary)] outline-none focus:border-primary';

export default function ForgotPassword() {
  const { t } = useTranslation('common');
  const [email, setEmail] = useState('');
  const [tenantCode, setTenantCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    try {
      await authService.forgotPassword({
        email: email.trim(),
        tenantCode: tenantCode.trim() || undefined,
      });
      setDone(true);
    } catch (ex: unknown) {
      const message =
        ex && typeof ex === 'object' && 'message' in ex
          ? String((ex as { message: unknown }).message)
          : t('status.error');
      setErr(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-6 py-16">
      <Link
        to="/login"
        className="mb-6 inline-flex items-center gap-1.5 text-sm text-[var(--color-text-secondary)] hover:text-primary"
      >
        <ArrowLeft size={14} /> Back to login
      </Link>

      <h1 className="text-2xl font-extrabold tracking-tight text-[var(--color-text-primary)]">
        Forgot your password?
      </h1>
      <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
        Enter your email and we'll send you a reset link.
      </p>

      {done ? (
        <div className="mt-8 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-700">
          <div className="flex items-center gap-2 font-semibold">
            <CheckCircle2 size={18} /> Check your inbox
          </div>
          <p className="mt-1 text-emerald-700/80">
            If an account exists for <strong>{email}</strong>, a reset link is on its way.
          </p>
        </div>
      ) : (
        <form className="mt-8 space-y-4" onSubmit={handleSubmit}>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-[var(--color-text-primary)]">
              {t('form.email')}
            </span>
            <div className="relative">
              <Mail
                size={16}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]"
              />
              <input
                type="email"
                required
                autoFocus
                className={`${inputCls} pl-9`}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
              />
            </div>
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-medium text-[var(--color-text-primary)]">
              Tenant code <span className="text-[var(--color-text-muted)]">(optional)</span>
            </span>
            <input
              type="text"
              className={inputCls}
              value={tenantCode}
              onChange={(e) => setTenantCode(e.target.value)}
              placeholder="e.g. acme"
            />
          </label>

          {err && (
            <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-600">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? t('status.saving') : 'Send reset link'}
          </button>
        </form>
      )}
    </div>
  );
}
